import ChartContainer from './ChartContainer'
import type { ChartPercentRow } from './types'
import ChakraTooltip from './ChakraTooltip'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from './chart/rc-theme'
import { Legend } from 'recharts'
import { toCssVar } from '../../theme/foundations/colors'

const percentTicks = Array.from({ length: 11 }, (_, i) => i * 10)

// Map series to Chakra semantic tokens (adjust to your theme)
const COLOR_MAP: Record<string, string> = {
  TOJ: 'green.solid',
  Beratung: 'cyan.solid',
  Schulung: 'purple.solid',
  Gesamt: 'blue.solid',
  Plan: 'orange.solid',
}

type Props = {
  title?: string
  height?: number
  data: ChartPercentRow[]
  // ticks: YearMonth[];
  ticks?: Array<string>
  isLoading?: boolean
  xKey?: string
  yAxisPercent?: boolean // if true, show 0..100% axis
}

const formatYearMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return String(ym)
  const shortMonth = new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(
    new Date(Date.UTC(y, m - 1, 1))
  )
  const shortYear = String(y).slice(-2)
  return `${shortMonth} ${shortYear}`
}

const LABEL: Record<string, string> = {
  TOJ: 'TOJ (Kunde)',
  Beratung: 'Beratung',
  Schulung: 'Schulung',
  Gesamt: 'Gesamt',
  Plan: 'Soll (Jahresverlauf)',
}

export default function TimeSeriesLineChart({
  // title = 'Arbeitszeit',
  height,
  xKey = 'month',
  data,
  ticks,
  isLoading,
  yAxisPercent = true,
}: Props) {
  const isEmpty = !data || data.length === 0
  const yAxisProps = yAxisPercent
    ? {
        domain: [0, 100] as any,
        ticks: percentTicks,
        tickFormatter: (v: any) => `${Math.round(v)}%`,
      }
    : { domain: ['auto', 'auto'] as any, tickFormatter: (v: any) => `${v}` }

  // Detect series dynamically from first row (exclude "month")
  // const seriesKeys = isEmpty ? [] : Object.keys(data[0] ?? {}).filter(k => k !== 'month')

  const firstRealRow = data.find(r => (r as any)[xKey] !== '__START__') ?? data[0]
  const seriesKeys = isEmpty ? [] : Object.keys(firstRealRow ?? {}).filter(k => k !== xKey)

  return (
    <ChartContainer h={height} isLoading={!!isLoading} isEmpty={isEmpty}>
      {!isEmpty && (
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            {/* <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 24 }}> */}
            <CartesianGrid strokeDasharray="3 3" />
            {/* <XAxis dataKey="month" tickMargin={8} ticks={ticks} /> */}
            <XAxis
              // padding={{ left: 24, right: 24 }}

              dataKey={xKey}
              type="category" // <-- treat x as strings
              allowDuplicatedCategory={false}
              ticks={ticks}
              // tickFormatter={formatYearMonth}
              tickFormatter={v => (v === '__START__' ? '' : String(v))}
              interval="preserveStartEnd" // ← show first and last if to many
              tickMargin={6}
              style={{ fontSize: '11px' }}
            />
            {/* <YAxis
              domain={[0, 100]}
              ticks={percentTicks}
              tickFormatter={v => `${v}%`}
              allowDecimals={false}
              width={48}
              style={{ fontSize: '11px' }}
            /> */}

            <YAxis {...yAxisProps} />
            <Tooltip content={<ChakraTooltip />} />
            <Legend
              verticalAlign="top"
              align="center"
              layout="horizontal"
              iconType="rect"
              wrapperStyle={{
                fontSize: '14px',
                lineHeight: '24px',
                paddingBottom: '12px',
              }}
            />

            {seriesKeys.map(key => {
              const isPlan = key === 'Plan'
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={LABEL[key] ?? key}
                  connectNulls={false}
                  // stroke={toCssVar(COLOR_MAP[key] ?? 'gray.solid')}
                  // strokeWidth={key === 'Gesamt' ? 3 : key === 'Plan' ? 2 : 2}
                  // strokeDasharray={key === 'Plan' ? '6 4' : undefined}
                  stroke={
                    isPlan
                      ? toCssVar('orange.solid') // always orange
                      : toCssVar(COLOR_MAP[key] ?? 'gray.solid')
                  }
                  strokeWidth={isPlan ? 2 : key === 'Gesamt' ? 3 : 2}
                  strokeDasharray={isPlan ? '6 4' : undefined}
                  // dot={key === 'Plan' ? false : { r: 3 }}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  legendType="rect"
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartContainer>
  )
}
